import { useRef, useState } from 'react';
import { api } from '../../api/client';
import type { MessageDemande } from '../../types';
import { BoutonCopier } from './BoutonCopier';
import { EtatMessage } from './EtatMessage';
import { attendUneAction, ligneEtat } from './format';
import { annoncerMiseAJourMessages } from './useCompteMessages';

/**
 * Un message Slack de recherche : son état en clair, ce qu'il dit, et — tant qu'il n'est pas parti — « Copier le message »
 * puis « Marquer comme publié » pour la personne qui doit le publier à la main (docs/DECISIONS.md §24).
 */
export function MessageSlack({ message: m, titre, onChange, copieEnAvant = true }: {
  message: MessageDemande; titre: string; onChange: () => void;
  /** « Copier le message » en bouton principal. Faux sur la fiche : la carte « Prochaine étape » porte déjà l'action principale (une seule par écran). */
  copieEnAvant?: boolean;
}) {
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [annonce, setAnnonce] = useState('');
  const [toutVoir, setToutVoir] = useState(false);
  const bloc = useRef<HTMLDivElement>(null);
  const attend = attendUneAction(m);

  async function marquer() {
    setEnCours(true);
    setErreur(null);
    try {
      await api.marquerMessageEnvoye(m.id);
      setAnnonce(`${titre} : marqué comme publié.`);
      annoncerMiseAJourMessages();
      onChange();
      // Le bouton disparaît : le focus reste sur le bloc au lieu de tomber sur la page (clavier, lecteur d'écran).
      bloc.current?.focus();
    } catch (err) {
      setErreur(err instanceof Error ? err.message : "L'enregistrement n'a pas abouti. Réessayez.");
      onChange();   // l'état affiché a peut-être changé entre-temps (quelqu'un d'autre l'a traité)
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="msg" data-etat={m.statut} ref={bloc} tabIndex={-1}>
      <div className="msg-haut">
        <span className="msg-canal msg-canal--slack">Slack</span>
        <b>{titre}</b>
        <EtatMessage statut={m.statut} libelle={m.libelle_etat} />
      </div>
      <div className="msg-meta">{ligneEtat(m)}</div>
      {m.statut !== 'plus_utile' && (
        <>
          <div className={`msg-apercu${toutVoir ? '' : ' msg-apercu--court'}`}>{m.corps}</div>
          <button type="button" className="msg-lien" aria-expanded={toutVoir} onClick={() => setToutVoir((v) => !v)}
            aria-label={`${toutVoir ? 'Réduire' : 'Afficher tout'} le message Slack (${titre})`}>
            {toutVoir ? 'Réduire' : 'Tout afficher'}
          </button>
        </>
      )}
      <div className="msg-actions">
        {m.statut !== 'plus_utile' && !(m.statut === 'envoye' && (m.mode_envoi === 'auto' || m.mode_envoi === 'simule')) && <BoutonCopier texte={m.corps} etiquette={`Copier le message Slack (${titre})`} accent={copieEnAvant && attend && m.peut_marquer} />}
        {attend && m.peut_marquer && (
          <button type="button" className="btn btn-sm btn-ghost" disabled={enCours} onClick={marquer} aria-label={`Marquer comme publié : ${titre}`}>
            {enCours ? <span className="spinner spinner-sm" role="status" aria-label="Enregistrement en cours" /> : 'Marquer comme publié'}
          </button>
        )}
        {attend && !m.peut_marquer && <span className="text-xs text-ink-3">{"Ce message est à publier par l'équipe gestion."}</span>}
      </div>
      <span className="sr-only" role="status">{annonce}</span>
      {erreur && <div className="text-xs text-err" role="alert">{erreur}</div>}
    </div>
  );
}
